import { useEffect, useState, useCallback } from 'react';
import type { Channel } from 'pusher-js';
import Pusher from 'pusher-js';
import PusherBatchAuthorizer from 'pusher-js-auth';
import axios from 'axios';
import errorReporter from '@app/errorReporter';
import type { PusherChannelTypes } from 'utils';
import {
  buildPusherChannel,
  NOTIFICATION_PREFIX,
  USER_CHANNEL_PREFIX,
  ONBOARDING_APPROVAL_PREFIX,
  COUNT_SOCKET_PREFIX,
} from 'utils';

// Due to pusher-js-auth not being a typed module..
// and authDelay doesn't exist in original Pusher type
declare module 'pusher-js' {
  interface Options {
    authDelay?: number;
  }
}

export type PusherInstance = Pusher;

const usePusher = (userId?: string, hasChat?: boolean, userEmail?: string) => {
  const [pusherInstance, setPusherInstance] = useState<PusherInstance>();
  const [pusherInfo, setPusherInfo] = useState({
    pusherAppKey: '',
    pusherCluster: '',
  });
  const [userChannel, setUserChannel] = useState<Channel>();
  const [notificationChannel, setNotificationChannel] = useState<Channel>();
  const [onboardingApprovalChannel, setOnboardingApprovalChannel] =
    useState<Channel>();
  const [countSocketChannel, setCountSocketChannel] = useState<Channel>();

  const fetchPusherInfo = async () => {
    try {
      const {
        data: { pusherAppKey, pusherCluster },
      } = await axios.get<{ pusherAppKey: string; pusherCluster: string }>(
        '/secrets/pusher',
      );
      setPusherInfo({ pusherAppKey, pusherCluster });
    } catch (err) {
      errorReporter.error(err);
    }
  };

  // Fetch static information
  useEffect(() => {
    fetchPusherInfo();
  }, []);

  // Initialize Pusher Instance
  useEffect(() => {
    const { pusherAppKey, pusherCluster } = pusherInfo;

    let pusher: PusherInstance | undefined;
    if (pusherAppKey && pusherCluster) {
      pusher = new Pusher(pusherAppKey, {
        cluster: pusherCluster,
        authorizer: PusherBatchAuthorizer,
        authDelay: 250,
      });
    }

    setPusherInstance(pusher);

    return () => {
      if (pusher) pusher.disconnect();
    };
  }, [pusherInfo]);

  // Notification Channel Handlers
  useEffect(() => {
    if (pusherInstance && userId) {
      const channelName = buildPusherChannel(NOTIFICATION_PREFIX, userId);

      const userConnect = () => {
        const newChannel = pusherInstance.subscribe(channelName);
        setNotificationChannel(newChannel);
      };

      userConnect();

      return () => {
        pusherInstance.unsubscribe(channelName);
        setNotificationChannel(undefined);
      };
    }
  }, [pusherInstance, userId]);

  // Messaging User Channel Handlers
  useEffect(() => {
    if (pusherInstance && userId && hasChat) {
      const channelName = buildPusherChannel(USER_CHANNEL_PREFIX, userId);

      const newChannel = pusherInstance.subscribe(channelName);
      setUserChannel(newChannel);

      return () => {
        pusherInstance.unsubscribe(channelName);
        setUserChannel(undefined);
      };
    }
  }, [pusherInstance, userId, hasChat]);

  // onboardingApproval Channel Handlers
  useEffect(() => {
    if (pusherInstance && userEmail) {
      const encodedEmail = btoa(userEmail);
      const channelName = buildPusherChannel(
        ONBOARDING_APPROVAL_PREFIX,
        encodedEmail,
      );

      const newChannel = pusherInstance.subscribe(channelName);
      setOnboardingApprovalChannel(newChannel);

      return () => {
        pusherInstance.unsubscribe(channelName);
        setOnboardingApprovalChannel(undefined);
      };
    }
  }, [pusherInstance, userEmail]);

  // countSocket Channel handlers
  useEffect(() => {
    if (pusherInstance && userId) {
      const channelName = buildPusherChannel(COUNT_SOCKET_PREFIX, userId);

      const newChannel = pusherInstance.subscribe(channelName);
      setCountSocketChannel(newChannel);

      return () => {
        pusherInstance.unsubscribe(channelName);
        setCountSocketChannel(undefined);
      };
    }
  }, [pusherInstance, userId]);

  // Generic Pusher event binder
  const setPusherEvents = useCallback(
    (
      channelType: PusherChannelTypes,
      eventName: string,
      cb: (arg: any) => void,
      isBind = true,
    ) => {
      const channelMap: Record<PusherChannelTypes, Channel | undefined> = {
        // TODO: Implement all types when below subscriptions are migrated to React
        aq: undefined,
        apt: undefined,
        convo: undefined,
        userim: userChannel,
        notification: notificationChannel,
        ongoingTransfer: undefined,
        listingCount: undefined,
        onboardingApproval: onboardingApprovalChannel,
        cs: countSocketChannel,
      };

      const channel = channelMap[channelType];
      if (!channel) return;

      channel[isBind ? 'bind' : 'unbind'](eventName, cb);
    },
    [
      userChannel,
      notificationChannel,
      onboardingApprovalChannel,
      countSocketChannel,
    ],
  );

  return {
    pusher: pusherInstance,
    setPusherEvents,
  };
};

export default usePusher;
